import mapValues from 'lodash/mapValues.js';
import pick from 'lodash/pick.js';
import { fixObfuscatedProp } from "./props.js";
export const authenticationFormConfig = {
    authenticationBearer: {
        default: {
            properties: {
                header: {
                    widget: 'sensitiveField',
                    instructions: 'Defaults to "Authorization"'
                },
                prefix: {
                    widget: 'sensitiveField',
                    instructions: 'Defaults to "Bearer"'
                },
                token: {
                    widget: 'sensitiveField'
                }
            },
            order: ['header', 'prefix', 'token']
        }
    },
    authenticationBasic: {
        default: {
            properties: {
                username: {
                    widget: 'sensitiveField'
                },
                password: {
                    widget: 'sensitiveField'
                }
            },
            order: ['username', 'password']
        }
    },
    authenticationSearchParams: {
        default: {
            properties: {
                paramName: {
                    widget: 'sensitiveField'
                },
                paramValue: {
                    widget: 'sensitiveField'
                }
            },
            order: ['paramName', 'paramValue']
        }
    },
    authenticationOauth2: {
        default: {
            properties: {
                clientId: {
                    widget: 'sensitiveField'
                },
                clientSecret: {
                    widget: 'sensitiveField'
                },
                accessTokenUrl: {
                    widget: 'sensitiveField'
                },
                scope: {
                    widget: 'sensitiveField'
                }
            },
            order: ['clientId', 'clientSecret', 'accessTokenUrl', 'scope']
        }
    }
};
export const authenticationDefinitions = {
    authenticationBearer: {
        title: 'Bearer Token',
        type: 'object',
        properties: {
            type: {
                title: 'Bearer Token',
                description: 'Bearer authentication uses a token sent in an HTTP header.',
                type: 'string',
                enum: ['bearer'],
                default: 'bearer'
            },
            token: {
                title: 'Token',
                description: 'The token to include in your header.',
                type: 'string'
            },
            header: {
                title: 'Header',
                description: 'The HTTP header to use when sending your token.',
                type: 'string'
            },
            prefix: {
                title: 'Prefix',
                description: 'Any key prefixes for your token.',
                type: 'string'
            }
        },
        required: ['type'],
        additionalProperties: false
    },
    authenticationBasic: {
        title: 'Basic',
        type: 'object',
        properties: {
            type: {
                title: 'Basic',
                description: 'Basic authentication uses an encoded username and password sent in the Authorization header.',
                type: 'string',
                enum: ['basic'],
                default: 'basic'
            },
            username: {
                title: 'Username',
                type: 'string'
            },
            password: {
                title: 'Password',
                type: 'string'
            }
        },
        required: ['type'],
        additionalProperties: false
    },
    authenticationSearchParams: {
        title: 'Search Params',
        type: 'object',
        properties: {
            type: {
                title: 'Search Params',
                description: 'Search params authentication sends a value in the query / search params appended to the endpoint.',
                type: 'string',
                enum: ['searchParams'],
                default: 'searchParams'
            },
            paramName: {
                title: 'Param Name',
                type: 'string'
            },
            paramValue: {
                title: 'Param Value',
                type: 'string'
            }
        },
        required: ['type'],
        additionalProperties: false
    },
    authenticationOauth2: {
        title: 'OAuth2 Client Credentials',
        type: 'object',
        properties: {
            type: {
                title: 'OAuth2 Client Credentials',
                description: 'Search params authentication sends a value in the query / search params appended to the endpoint.',
                type: 'string',
                enum: ['oauth2'],
                default: 'oauth2'
            },
            clientId: {
                title: 'Client Id',
                type: 'string'
            },
            clientSecret: {
                title: 'Client Secret',
                type: 'string'
            },
            accessTokenUrl: {
                title: 'Access Token Url',
                type: 'string'
            },
            scope: {
                title: 'Scope',
                type: 'string'
            }
        },
        required: ['type', 'clientId', 'clientSecret', 'accessTokenUrl'],
        additionalProperties: false
    },
    authenticationNone: {
        title: 'None',
        type: 'object',
        properties: {
            type: {
                title: 'None',
                description: 'Use no authentication.',
                type: 'string',
                enum: ['none'],
                default: 'none'
            }
        },
        required: ['type'],
        additionalProperties: false
    }
};
export function getAuthenticationDefinitions(paths) {
    return pick(authenticationDefinitions, paths);
}
export function getAuthenticationProperties(paths) {
    const oneOf = paths.map((definition) => ({
        $ref: `#/definitions/${definition}`
    }));
    const properties = {
        authentication: {
            title: 'Authentication',
            discriminator: {
                propertyName: 'type'
            },
            oneOf
        }
    };
    return properties;
}
export function addAuthenticationSchema(schema) {
    const paths = Object.keys(authenticationDefinitions);
    const definitions = getAuthenticationDefinitions(paths);
    const properties = getAuthenticationProperties(paths);
    return {
        ...schema,
        definitions: {
            ...schema.definitions,
            ...definitions
        },
        properties: {
            ...schema.properties,
            ...properties
        }
    };
}
const authenticationFormProps = {
    authentication: {
        instructions: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
        isCollapsible: true,
        isCollapsed: true
    }
};
export function addAuthenticationFormConfig(formConfig) {
    const { properties, order } = formConfig;
    if (properties) {
        const newOrder = Object.keys(authenticationFormProps);
        return {
            ...formConfig,
            properties: {
                ...properties,
                ...authenticationFormProps
            },
            order: order ? [...order, ...newOrder] : newOrder
        };
    }
    return formConfig;
}
function getAuthenticationUpdate(formData, constantAuthParams) {
    const { authentication } = formData;
    const authenticationType = authentication?.type ?? constantAuthParams.type;
    // Null out `authentication` which will remove any existing values (whereas `undefined` will leave them untouched).
    if (!authentication || authenticationType === 'none') {
        return {
            authenticationType: authenticationType ?? 'none',
            authentication: null
        };
    }
    if (authenticationType === 'basic') {
        return {
            authenticationType: 'basic',
            authentication: {
                type: 'basic',
                username: fixObfuscatedProp(authentication.username),
                password: fixObfuscatedProp(authentication.password),
                ...constantAuthParams
            }
        };
    }
    if (authenticationType === 'bearer') {
        return {
            authenticationType: 'bearer',
            authentication: {
                type: 'bearer',
                token: fixObfuscatedProp(authentication.token),
                header: fixObfuscatedProp(authentication.header),
                prefix: fixObfuscatedProp(authentication.prefix),
                ...constantAuthParams
            }
        };
    }
    if (authenticationType === 'searchParams') {
        // Legacy, ideally the form would enforce and provide the array style
        const name = fixObfuscatedProp(authentication.paramName);
        const value = fixObfuscatedProp(authentication.paramValue);
        if (name && value) {
            return {
                authenticationType: 'searchParams',
                authentication: {
                    type: 'searchParams',
                    params: [{ name, value }],
                    ...constantAuthParams
                }
            };
        }
    }
    if (authenticationType === 'aws') {
        return {
            authenticationType: 'aws',
            authentication: {
                type: 'aws',
                awsAccessKeyId: fixObfuscatedProp(authentication.awsAccessKeyId),
                awsSecretAccessKey: fixObfuscatedProp(authentication.awsSecretAccessKey),
                ...constantAuthParams
            }
        };
    }
    if (authenticationType === 'google') {
        return {
            authenticationType: 'google',
            authentication: {
                type: 'google',
                clientEmail: fixObfuscatedProp(authentication.clientEmail),
                privateKey: fixObfuscatedProp(authentication.privateKey),
                ...constantAuthParams
            }
        };
    }
    if (authenticationType === 'oauth2') {
        return {
            authenticationType: 'oauth2',
            authentication: {
                type: 'oauth2',
                grantType: 'clientCredentials',
                clientId: fixObfuscatedProp(authentication.clientId),
                clientSecret: fixObfuscatedProp(authentication.clientSecret),
                accessTokenUrl: fixObfuscatedProp(authentication.accessTokenUrl),
                scope: fixObfuscatedProp(authentication.scope),
                ...constantAuthParams
            }
        };
    }
    if (authenticationType === 'custom') {
        return {
            authenticationType: 'custom',
            authentication: {
                type: 'custom',
                ...mapValues(authentication, fixObfuscatedProp),
                ...constantAuthParams
            }
        };
    }
}
export function updateAuthentication(config, formData, constantAuthParams = {}) {
    const authenticationUpdate = getAuthenticationUpdate(formData, constantAuthParams);
    if (authenticationUpdate !== undefined) {
        return {
            ...config,
            ...authenticationUpdate
        };
    }
    return config;
}
